import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const ExternalisationAirbnb = () => {
  return (
    <>
      <Helmet>
        <title>Externalisation appels AirBnB - ZiExter | Conciergerie locative</title>
        <meta name="description" content="Gérez vos locations AirBnB sans stress : réponses voyageurs 24/7, gestion réservations, check-in/out, support multilingue. Votre propriété, notre expertise." />
        <link rel="canonical" href="https://ziexter.com/services/externalisation-appels-airbnb" />
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Externalisation AirBnB</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Externalisation appels Airbnb – ZiExter
            </h1>
            <p className="text-xl text-gray-100 mb-4">
              Votre propriété, notre expertise : gestion complète des appels et de la relation voyageurs Airbnb
            </p>
            <p className="text-lg text-gray-200">
              Vous manquez de temps pour répondre à vos voyageurs ? Vos réservations sont irrégulières à cause d'un manque de réactivité ? Votre annonce perd en visibilité ?
            </p>
            <p className="text-lg text-gray-200 mt-4">
              Alors, confiez vos appels Airbnb à ZiExter, votre partenaire d'externalisation, et garantissez un service client 24/7, des voyageurs satisfaits et un taux de réservation optimisé !
            </p>
          </div>
        </div>
      </section>

      {/* CTA Hero */}
      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">
              Demander un devis
            </Link>
            <Link to="/rendez-vous" className="btn btn-outline-white">
              Planifiez une consultation gratuite dès maintenant
            </Link>
          </div>
        </div>
      </section>

      {/* De l'ordinaire à l'extraordinaire */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">De l'ordinaire à l'extraordinaire : optimisez votre Airbnb</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg mb-4">
                Mettre un bien en location saisonnière exige une réactivité constante et une excellente communication avec les voyageurs.
              </p>
              <p className="text-lg mb-4">
                Un appel manqué peut coûter une réservation et un mauvais avis peut réduire vos chances d'attirer de nouveaux clients.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg my-6">
                <p className="text-xl font-semibold text-primary mb-4">Avec ZiExter :</p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 text-xl">✅</span>
                    Nous gérons vos appels entrants et sortants.
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 text-xl">✅</span>
                    Nous assistons vos voyageurs avant, pendant et après leur séjour.
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 text-xl">✅</span>
                    Nous garantissons des réponses rapides et professionnelles 24/7.
                  </li>
                </ul>
              </div>
              <p className="text-lg">
                Notre équipe formée aux exigences Airbnb prend en charge la communication avec vos voyageurs, la coordination des arrivées et départs, et le suivi de la satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi externaliser */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Pourquoi externaliser vos appels Airbnb à ZiExter ?</h2>
            <p className="text-lg text-gray-600 mb-6">
              Rejoignez des centaines d'hôtes qui nous font déjà confiance pour :
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Assurer une disponibilité téléphonique permanente.',
              'Répondre aux questions fréquentes sur l\'annonce, l\'accès ou les équipements.',
              'Coordonner les arrivées et départs des voyageurs.',
              'Gérer les urgences (retard, panne, problème d\'accès, réclamation).',
              'Maximiser vos chances d\'obtenir le badge Superhost.'
            ].map((item, i) => (
              <div key={i} className="card hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-primary mr-3 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-gray-700">{item}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-lg font-semibold text-primary mt-8">
            Avec ZiExter, vous restez serein tout en augmentant vos revenus locatifs.
          </p>
        </div>
      </section>

      {/* Témoignages */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Témoignages de nos clients satisfaits</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <div className="font-bold mb-2">Michael R. – Hôte Airbnb à Paris</div>
              <p className="text-gray-700 italic">
                « Avant ZiExter, je ratais trop d'appels et perdais des réservations. Aujourd'hui, mes voyageurs ont toujours quelqu'un pour répondre et mon taux d'occupation a explosé. »
              </p>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <div className="font-bold mb-2">Hazel C. – Propriétaire à Lyon</div>
              <p className="text-gray-700 italic">
                « Leur équipe gère la communication avec mes voyageurs de A à Z. Je n'ai plus à me soucier des imprévus et mes notes sont passées de 4,5 à 4,9 étoiles. »
              </p>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <div className="font-bold mb-2">Louise H. – Multi-hôtesse Airbnb</div>
              <p className="text-gray-700 italic">
                « Avec plusieurs biens en location, je n'arrivais plus à tout gérer. ZiExter s'occupe des appels et de la coordination, je gagne du temps et de l'argent. »
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos services d'externalisation pour hôtes Airbnb</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="card hover-lift animate-fade-in-up">
              <h3 className="font-bold text-lg mb-3">✔️ Gestion des appels voyageurs</h3>
              <p className="text-gray-600">Disponibilité 24/7 pour répondre aux questions.</p>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-50">
              <h3 className="font-bold text-lg mb-3">✔️ Assistance check-in / check-out</h3>
              <p className="text-gray-600">Coordination fluide des arrivées et départs.</p>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <h3 className="font-bold text-lg mb-3">✔️ Suivi des réclamations</h3>
              <p className="text-gray-600">Résolution rapide pour éviter les avis négatifs.</p>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-150">
              <h3 className="font-bold text-lg mb-3">✔️ Optimisation du calendrier et de la tarification</h3>
              <p className="text-gray-600">Maximisez vos revenus avec notre expertise.</p>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <h3 className="font-bold text-lg mb-3">✔️ Support multilingue</h3>
              <p className="text-gray-600">Pour accueillir des voyageurs du monde entier.</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p className="text-lg font-semibold text-primary mb-4">Grâce à nos services, vous bénéficiez :</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                D'un taux de réponse supérieur à 95%.
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                D'une réduction des annulations.
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">•</span>
                D'une meilleure visibilité et attractivité de votre annonce.
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Questions fréquentes (FAQ)</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Quels services inclut l'externalisation des appels Airbnb ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Nous couvrons la gestion complète des appels : information voyageurs, coordination des séjours, traitement des urgences, suivi post-séjour et reporting régulier pour les hôtes.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Combien coûtent vos services ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Nos forfaits sont adaptés à vos besoins (nombre de biens, volume d'appels). Contactez-nous pour un devis personnalisé.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Vais-je garder le contrôle sur mon annonce Airbnb ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Bien sûr ! Vous restez propriétaire et gestionnaire de votre annonce. Nous vous assistons uniquement dans la gestion des appels et de la relation voyageurs.
              </div>
            </details>

            <details className="card cursor-pointer">
              <summary className="font-semibold p-4 flex justify-between items-center">
                <span>Comment augmenter mes réservations grâce à ZiExter ?</span>
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="p-4 pt-0 text-gray-600">
                Avec un service client disponible et réactif, vos voyageurs sont satisfaits, vos avis s'améliorent, et votre annonce gagne en visibilité sur Airbnb.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Externalisation appels Airbnb avec ZiExter
            </h2>
            <p className="text-2xl text-gray-100 mb-4">
              🎯 Plus de réservations, plus de bénéfices — moins de stress pour vous !
            </p>
            <p className="text-xl text-gray-100 mb-8">
              Notre mission : vous offrir la tranquillité d'esprit tout en maximisant vos revenus Airbnb.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn btn-accent btn-lg">
                Contactez ZiExter dès aujourd'hui
              </Link>
              <Link to="/devis" className="btn btn-outline-white btn-lg">
                Demander un devis
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ExternalisationAirbnb;
